import React, {
  createContext,
  Dispatch,
  ReducerAction,
  ReducerState,
  useCallback,
  useContext,
  useReducer,
  useState,
} from 'react';
import SimpleDialog from '../components/dialogs/SimpleDialog';
import { useIntl } from './intl';

// State

export type DialogType = 'confirm' | 'alert';

interface DialogProps {
  title: string;
  content: React.ReactNode | string;
  mainActionLabel: string;
  cancelLabel: string;
  onMainAction: () => void | Promise<any>;
  onCancel: () => void;
}

type DialogsState = {
  open: boolean;
  type: DialogType;
  props: DialogProps;
};

const defaultAlertDialogProps: DialogProps = {
  title: '',
  content: '',
  mainActionLabel: 'OK',
  cancelLabel: '',
  onMainAction: () => {},
  onCancel: () => {},
};

const getDefaultConfirmDialogProps = (
  confirmLabel: string,
  cancelLabel: string
): DialogProps => ({
  title: '',
  content: '',
  mainActionLabel: confirmLabel,
  cancelLabel,
  onMainAction: () => {},
  onCancel: () => {},
});

const initialState: DialogsState = {
  open: false,
  type: 'alert',
  props: defaultAlertDialogProps,
};

// Actions

const OPEN_CONFIRM_DIALOG_ACTION_TYPE = 'OPEN_CONFIRM_DIALOG';

type OpenConfirmDialogAction = {
  type: 'OPEN_CONFIRM_DIALOG';
  props: Partial<DialogProps>;
};

function getOpenConfirmDialogAction(
  props: Partial<DialogProps>
): OpenConfirmDialogAction {
  return {
    type: OPEN_CONFIRM_DIALOG_ACTION_TYPE,
    props,
  };
}

const OPEN_ALERT_DIALOG_ACTION_TYPE = 'OPEN_ALERT_DIALOG';

type OpenAlertDialogAction = {
  type: 'OPEN_ALERT_DIALOG';
  props: Partial<DialogProps>;
};

function getOpenAlertDialogAction(
  props: Partial<DialogProps>
): OpenAlertDialogAction {
  return {
    type: OPEN_ALERT_DIALOG_ACTION_TYPE,
    props,
  };
}

const CLOSE_DIALOG_ACTION_TYPE = 'CLOSE_DIALOG';

type CloseDialogAction = {
  type: 'CLOSE_DIALOG';
};

const getCloseDialogAction = (): CloseDialogAction => ({
  type: CLOSE_DIALOG_ACTION_TYPE,
});

type DialogsAction =
  | OpenConfirmDialogAction
  | OpenAlertDialogAction
  | CloseDialogAction;

// Reducer

type DialogsStateReducer = (
  state: DialogsState,
  action: DialogsAction
) => DialogsState;

function dialogsReducer(
  state: DialogsState,
  action: DialogsAction
): DialogsState {
  if (action.type === OPEN_CONFIRM_DIALOG_ACTION_TYPE) {
    return {
      open: true,
      type: 'confirm',
      props: {
        ...defaultAlertDialogProps,
        ...action.props,
      },
    };
  }

  if (action.type === OPEN_ALERT_DIALOG_ACTION_TYPE) {
    return {
      open: true,
      type: 'alert',
      props: {
        ...defaultAlertDialogProps,
        ...action.props,
      },
    };
  }

  if (action.type === CLOSE_DIALOG_ACTION_TYPE) {
    return {
      open: false,
      type: state.type,
      props: state.props,
    };
  }

  return state;
}

// Context

type DialogsContextState = [
  ReducerState<DialogsStateReducer>,
  Dispatch<ReducerAction<DialogsStateReducer>>
];
const DialogsContext = createContext<DialogsContextState>([
  initialState,
  () => {},
]);

// Provider

export const DialogsProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(dialogsReducer, initialState);
  const [loading, setLoading] = useState(false);

  const { open, type, props } = state;

  const handleClose = () => {
    dispatch(getCloseDialogAction());
  };

  const handleMainAction = async () => {
    const res = props.onMainAction();
    if (res instanceof Promise) {
      setLoading(true);
      await res;
    }
    handleClose();
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const handleCancelConfirm = () => {
    handleClose();
    props.onCancel();
  };

  return (
    <DialogsContext.Provider value={[state, dispatch]}>
      {children}

      <SimpleDialog
        type={type}
        title={props.title}
        content={props.content}
        loading={loading}
        open={open}
        mainActionLabel={props.mainActionLabel}
        cancelLabel={props.cancelLabel}
        onMainAction={handleMainAction}
        onCancel={handleCancelConfirm}
      />
    </DialogsContext.Provider>
  );
};

export const useDialogs = () => {
  const [state, dispatch] = useContext<DialogsContextState>(DialogsContext);
  const {
    txc: {
      actions: { cancel: cancelLabel, confirm: confirmLabel },
    },
  } = useIntl();

  const alert = useCallback(
    (props: Partial<DialogProps>) => {
      if (state.open) {
        dispatch(getCloseDialogAction());
      }

      const finalProps = {
        ...defaultAlertDialogProps,
        ...props,
      };

      dispatch(getOpenAlertDialogAction(finalProps));
    },
    [dispatch, state.open]
  );

  const confirm = useCallback(
    (props: Partial<DialogProps>) => {
      if (state.open) {
        dispatch(getCloseDialogAction());
      }

      const finalProps = {
        ...getDefaultConfirmDialogProps(confirmLabel, cancelLabel),
        ...props,
      };

      dispatch(getOpenConfirmDialogAction(finalProps));
    },
    [cancelLabel, confirmLabel, dispatch, state.open]
  );

  return {
    confirm,
    alert,
  };
};
